/**
 * Button — primitive du design system.
 *
 * 6 variants alignés sur globals.css (Direction B éditoriale) :
 *   primary  · rouge sang, action principale (un seul par écran)
 *   neutral  · encre solide, action secondaire forte
 *   ghost    · contour encre, fond transparent
 *   quiet    · sans bordure, hover gris
 *   accent   · rouge, accent éditorial
 *   danger   · rouge plein, destructif
 *
 * 3 sizes : sm · md (default) · lg.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'neutral' | 'ghost' | 'quiet' | 'accent' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  loading?: boolean
  children?: ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'neutral',
    size = 'md',
    block = false,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    style,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading
  const classes = [
    'ui-button',
    `ui-button--${variant}`,
    size !== 'md' && `ui-button--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={classes}
      style={block ? { width: '100%', ...style } : style}
      {...rest}
    >
      {loading ? '…' : children}
    </button>
  )
})

export default Button
